/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useContext, useEffect, useRef } from "react";
import * as ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { IAppState } from "../../../core/redux/IAppState";
import { getResource } from "../../../language/resources/getResource";
import { ReactComponent as CheckIcon } from "../../../assets/icons/checkCircleFilled.svg";
import { ReactComponent as ErrorIcon } from "../../../assets/icons/xCircleRed.svg";
import * as Buttons from "../../../forms/elements/buttons";
import {
  COOLER_GREY,
  DARK_GREY,
  DARK_RED_CREEK,
  IKON_BLUE,
  LIGHT_GREY,
  WHITE,
} from "../../../styles/colors";
import { LAYER } from "../../../layout/zIndex";
import { PortalTargetContext } from "../../../layout/modal/portalTarget";
import { LiftAccessRuleSet } from "./models";
import { describeSaveErrorCode, isRetryableSaveError } from "../saveErrorCodes";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

const overlayCss = css`
  position: fixed;
  inset: 0;
  z-index: ${LAYER.TWO.MODAL_BASE};
  background: rgb(0 0 0 / 15%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const cardCss = css`
  background: ${WHITE};
  padding: 24px 28px;
  width: min(720px, 92vw);
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid ${LIGHT_GREY};
  box-shadow: 0 6px 24px rgb(0 0 0 / 20%);
`;

const titleCss = css`
  font-size: 18px;
  font-weight: 600;
  color: ${IKON_BLUE};
  text-align: center;
  margin-bottom: 12px;
`;

const statsCss = css`
  display: flex;
  justify-content: center;
  gap: 18px;
  font-size: 15px;
  margin-bottom: 14px;

  .stat {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const hintCss = css`
  font-size: 13px;
  color: ${DARK_GREY};
  margin-bottom: 12px;
`;

const gridScrollCss = css`
  overflow: auto;
  border: 1px solid ${COOLER_GREY};
  border-radius: 6px;
`;

const gridTableCss = css`
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: 12px;

  th,
  td {
    padding: 6px 10px;
    text-align: left;
    vertical-align: top;
    white-space: nowrap;
  }

  thead th {
    position: sticky;
    top: 0;
    background: ${LIGHT_GREY};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: ${DARK_GREY};
    border-bottom: 1px solid ${COOLER_GREY};
  }

  tbody td {
    border-bottom: 1px solid ${LIGHT_GREY};
    color: ${IKON_BLUE};
  }

  td.description {
    white-space: normal;
    max-width: 260px;
  }

  td.error {
    white-space: normal;
    max-width: 240px;
    color: ${DARK_RED_CREEK};
  }

  td.center {
    text-align: center;
  }

  .row-flag {
    color: ${DARK_RED_CREEK};
  }
`;

const footerCss = css`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

/** Only the columns this screen shows. */
export type BulkFailureIdentity = Pick<
  LiftAccessRuleSet,
  | "entitlementTypeId"
  | "entitlementDescription"
  | "sellingSeasonDescription"
  | "fulfillmentSeasonDescription"
  | "locationName"
>;

export type BulkSaveFailure = {
  ruleSet: BulkFailureIdentity;
  errorStatus?: number;
  errorCode?: string;
  errorMessage?: string;
};

/** Which bulk operation's outcome is being reported. */
export type BulkOperation = "add" | "save" | "delete";

/**
 * The only copy that differs between operations; everything else reads the same.
 * Parameterised rather than forked, so the next accessibility or layout fix lands on
 * both.
 */
const OPERATION_COPY: Record<
  BulkOperation,
  {
    title: string;
    stoppedTitle: string;
    cancelledTitle: string;
    succeeded: string;
    hint: string;
    fixNote: string;
    /** What a cancelled run says about the rule sets it never reached. */
    cancelledNote: string;
    unknownError: string;
    retryTitle: string;
  }
> = {
  add: {
    title: "ACCESS_RULE_BULK_ADD_FAILURES_TITLE",
    stoppedTitle: "ACCESS_RULE_BULK_ADD_STOPPED_TITLE",
    cancelledTitle: "ACCESS_RULE_BULK_ADD_CANCELLED_TITLE",
    succeeded: "ACCESS_RULE_BULK_ADD_FAILURES_SUCCEEDED",
    hint: "ACCESS_RULE_BULK_ADD_FAILURES_HINT",
    fixNote: "ACCESS_RULE_BULK_ADD_FAILURES_FIX_NOTE",
    cancelledNote: "ACCESS_RULE_BULK_ADD_CANCELLED_NOTE",
    unknownError: "ACCESS_RULE_BULK_ADD_FAILURES_UNKNOWN",
    retryTitle: "ACCESS_RULE_BULK_ADD_FAILURES_RETRY_TITLE",
  },
  save: {
    title: "ACCESS_RULE_BULK_EDIT_FAILURES_TITLE",
    stoppedTitle: "ACCESS_RULE_BULK_EDIT_FAILURES_TITLE",
    cancelledTitle: "ACCESS_RULE_BULK_EDIT_FAILURES_TITLE",
    succeeded: "ACCESS_RULE_BULK_EDIT_FAILURES_SUCCEEDED",
    hint: "ACCESS_RULE_BULK_EDIT_FAILURES_HINT",
    fixNote: "ACCESS_RULE_BULK_EDIT_FAILURES_FIX_NOTE",
    // A stopped save reads the same whether the user or the breaker stopped it.
    cancelledNote: "ACCESS_RULE_BULK_EDIT_FAILURES_NOT_ATTEMPTED_NOTE",
    unknownError: "ACCESS_RULE_BULK_EDIT_FAILURES_UNKNOWN",
    retryTitle: "ACCESS_RULE_BULK_EDIT_FAILURES_RETRY_TITLE",
  },
  delete: {
    title: "ACCESS_RULE_BULK_DELETE_FAILURES_TITLE",
    stoppedTitle: "ACCESS_RULE_BULK_DELETE_STOPPED_TITLE",
    cancelledTitle: "ACCESS_RULE_BULK_DELETE_CANCELLED_TITLE",
    succeeded: "ACCESS_RULE_BULK_DELETE_FAILURES_SUCCEEDED",
    hint: "ACCESS_RULE_BULK_DELETE_FAILURES_HINT",
    fixNote: "ACCESS_RULE_BULK_DELETE_FAILURES_FIX_NOTE",
    cancelledNote: "ACCESS_RULE_BULK_DELETE_CANCELLED_NOTE",
    unknownError: "ACCESS_RULE_BULK_DELETE_FAILURES_UNKNOWN",
    retryTitle: "ACCESS_RULE_BULK_DELETE_FAILURES_RETRY_TITLE",
  },
};

type Props = {
  failures: BulkSaveFailure[];
  successCount: number;
  failureCount: number;
  /** Rule sets the run never reached because it stopped early. */
  notAttemptedCount?: number;
  /**
   * How many rule sets Retry would send. Defaults to the retryable failures listed
   * below; pass it when the run also left rule sets unattempted, which aren't listed
   * here but are equally worth sending again.
   */
  retryableCount?: number;
  /** Which operation's copy to use. Defaults to the bulk edit save. */
  operation?: BulkOperation;
  /**
   * The user stopped the run. Nothing went wrong, so the error framing doesn't fit —
   * and for a delete the copy has to say the deleted rule sets are still deleted.
   */
  cancelled?: boolean;
  /** Re-run the save for the retryable rule sets. Omit to hide Retry. */
  onRetry?: () => void;
  onClose: () => void;
};

/**
 * Post-save screen listing which rule sets didn't save and why. A bulk save fans out
 * one request per rule set, so "3 failed" out of 60 is useless without the list.
 */
export function BulkSaveFailuresView({
  failures,
  successCount,
  failureCount,
  notAttemptedCount = 0,
  retryableCount,
  operation = "save",
  cancelled = false,
  onRetry,
  onClose,
}: Props) {
  const copy = OPERATION_COPY[operation];
  const resources = useSelector((s: IAppState) => s.locale);
  const portalTarget = useContext(PortalTargetContext) as Document | null;
  const doc = portalTarget ?? document;
  const dialogRef = useRef<HTMLDivElement>(null);

  const title = getResource(
    resources,
    cancelled
      ? copy.cancelledTitle
      : // Nothing to list when a run was stopped without failing: the screen then
        // reports what didn't run rather than what failed.
        failureCount > 0
        ? copy.title
        : copy.stoppedTitle,
  );

  useEffect(() => {
    const previouslyFocused = doc.activeElement as HTMLElement | null;
    const card = dialogRef.current;

    const focusables = () =>
      Array.from(card?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);

    (focusables()[0] ?? card)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      // Trapped so focus can't reach the page behind this overlay.
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        card?.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = doc.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    doc.addEventListener("keydown", onKeyDown, true);
    return () => {
      doc.removeEventListener("keydown", onKeyDown, true);
      previouslyFocused?.focus?.();
    };
    // Trap is established once per open; deps intentionally minimal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // From the failures themselves, so this and the hint below can't disagree.
  const retryableFailureCount = failures.filter(isRetryableSaveError).length;
  const retryCount = retryableCount ?? retryableFailureCount;
  const canRetry = !!onRetry && retryCount > 0;
  // More to send than failed retryably means the rest were never attempted, and
  // "Retry n failed" would misdescribe them.
  const retryIsNotOnlyFailures = retryCount > retryableFailureCount;

  const hintSentences = [
    failureCount > 0 && getResource(resources, copy.hint),
    retryableFailureCount > 0 &&
      getResource(resources, "ACCESS_RULE_BULK_EDIT_FAILURES_RETRY_NOTE"),
    retryableFailureCount < failureCount &&
      getResource(resources, copy.fixNote),
    notAttemptedCount > 0 &&
      // A cancelled run stopped on purpose, so it doesn't read as a malfunction —
      // but for a delete it still has to say the rest are already gone.
      getResource(
        resources,
        cancelled
          ? copy.cancelledNote
          : "ACCESS_RULE_BULK_EDIT_FAILURES_NOT_ATTEMPTED_NOTE",
        notAttemptedCount,
      ),
  ].filter((s): s is string => !!s);

  const errorText = (f: BulkSaveFailure): string => {
    // Prefer the API's structured error code, mapped to a friendly message.
    const codeMessage = describeSaveErrorCode(f.errorCode, resources);
    if (codeMessage) return codeMessage;
    if (f.errorMessage) {
      return f.errorStatus ? `${f.errorStatus} — ${f.errorMessage}` : f.errorMessage;
    }
    if (f.errorStatus) {
      return getResource(resources, "ACCESS_RULE_BULK_EDIT_FAILURES_HTTP", f.errorStatus);
    }
    return getResource(resources, copy.unknownError);
  };

  return ReactDOM.createPortal(
    <div css={overlayCss}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        css={cardCss}
      >
        <div css={titleCss}>{title}</div>

        <div css={statsCss}>
          <span className="stat">
            <CheckIcon aria-hidden="true" />
            {getResource(resources, copy.succeeded, successCount)}
          </span>
          {/* Omitted at zero: a red cross beside "0 failed" reads as an error, and a
              cancelled run reaches this screen with nothing failed. */}
          {failureCount > 0 && (
            <span className="stat">
              <ErrorIcon aria-hidden="true" />
              {getResource(
                resources,
                "ACCESS_RULE_BULK_EDIT_FAILURES_FAILED",
                failureCount,
              )}
            </span>
          )}
          {/* Only when the run stopped early; at zero it reads as rule sets going missing. */}
          {notAttemptedCount > 0 && (
            <span className="stat">
              {getResource(
                resources,
                "ACCESS_RULE_BULK_EDIT_FAILURES_NOT_ATTEMPTED",
                notAttemptedCount,
              )}
            </span>
          )}
        </div>

        <div css={hintCss}>{hintSentences.join(" ")}</div>

        {/* A run stopped without failing has nothing to list; the stats are the report. */}
        {failures.length > 0 && (
        <div css={gridScrollCss}>
          <table css={gridTableCss}>
            <thead>
              <tr>
                <th scope="col">
                  {getResource(resources, "LIFT_ACCESS_RULES_TABLE_ID")}
                </th>
                <th scope="col">
                  {getResource(resources, "LIFT_ACCESS_RULES_TABLE_DESCRIPTION")}
                </th>
                <th scope="col">
                  {getResource(resources, "LIFT_ACCESS_RULES_TABLE_SEASON")}
                </th>
                <th scope="col">
                  {getResource(
                    resources,
                    "LIFT_ACCESS_RULES_TABLE_FULFILLMENT_SEASON",
                  )}
                </th>
                <th scope="col">
                  {getResource(resources, "LIFT_ACCESS_RULES_TABLE_LOCATION")}
                </th>
                <th scope="col">
                  {getResource(resources, "ACCESS_RULE_BULK_EDIT_FAILURES_ERROR")}
                </th>
              </tr>
            </thead>
            <tbody>
              {failures.map((f, idx) => (
                <tr key={idx}>
                  <td className="center row-flag">{f.ruleSet.entitlementTypeId}</td>
                  <td className="description">{f.ruleSet.entitlementDescription}</td>
                  <td className="center">{f.ruleSet.sellingSeasonDescription}</td>
                  <td>{f.ruleSet.fulfillmentSeasonDescription}</td>
                  <td>{f.ruleSet.locationName}</td>
                  <td className="error">{errorText(f)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        <div css={footerCss}>
          <Buttons.BigSecondary
            title={getResource(
              resources,
              "ACCESS_RULE_BULK_EDIT_FAILURES_CLOSE_TITLE",
            )}
            onClick={onClose}
          >
            {getResource(resources, "ACCESS_RULE_BULK_EDIT_CLOSE")}
          </Buttons.BigSecondary>

          {canRetry && (
            <Buttons.BigNeutral
              title={getResource(
                resources,
                retryIsNotOnlyFailures
                  ? "ACCESS_RULE_BULK_RETRY_NOT_ATTEMPTED_TITLE"
                  : copy.retryTitle,
                retryCount,
              )}
              onClick={onRetry}
            >
              {getResource(
                resources,
                retryIsNotOnlyFailures
                  ? "ACCESS_RULE_BULK_RETRY_NOT_ATTEMPTED"
                  : "ACCESS_RULE_BULK_EDIT_FAILURES_RETRY",
                retryCount,
              )}
            </Buttons.BigNeutral>
          )}
        </div>
      </div>
    </div>,
    doc.body,
  );
}
